import { INHOUSE_LTX23_MODEL, VideoPricingMode, VideoPricingTaskType, cloneParameters, getVideoPricingMode, isDITModelType, lookupVideoPerSecondPriceItem } from "./util.mjs";
import { BasePricing } from "./base.mjs";
import { EditPricing } from "./editGen.mjs";
//#region src/videoGen.ts
var I2VProPricing = class extends BasePricing {
	clonedI2vProParameters;
	constructor(parameters, config) {
		super(parameters, config);
		this.clonedI2vProParameters = cloneParameters(parameters.i2vPro);
	}
	getActualBasePrice() {
		const { basePrice, modelType } = this.config;
		if (isDITModelType(modelType)) {
			const { width, height, samplingSteps } = this.parameters;
			return basePrice * ([
				[768, 1280],
				[1280, 768],
				[1024, 1024]
			].some(([w, h]) => w === width && h === height) && samplingSteps >= 20 && samplingSteps <= 25 ? 1.5 : 1.6);
		}
		return basePrice;
	}
	calcGenerateImageBasePrice() {
		const actualBasePrice = this.getActualBasePrice();
		return {
			cumulative: () => actualBasePrice,
			item: {
				key: "generate-image",
				name: "generate image"
			}
		};
	}
	getFinalPrice() {
		this.meter.addResult(this.calcGenerateImageBasePrice());
		this._addAudioPrice();
		const perSecondPriceResults = this.calcPerSecondPrice();
		if (perSecondPriceResults.length) {
			for (const result of perSecondPriceResults) this.meter.addResult(result);
			return this.meter;
		}
		this.meter.addResult(this.calcOriginalPrice());
		this.meter.addResult(this.calcDiscountedPrice());
		return this.meter;
	}
	calcPerSecondPrice() {
		const model = this.clonedI2vProParameters.model;
		if (getVideoPricingMode(this.config, VideoPricingTaskType.I2vPro, model) !== VideoPricingMode.PerSecond) return [];
		const durationRaw = this.clonedI2vProParameters.duration;
		const duration = typeof durationRaw === "string" ? Number.parseInt(durationRaw, 10) : Number(durationRaw);
		if (!Number.isFinite(duration) || duration <= 0) return [];
		const mode = this.clonedI2vProParameters.mode;
		const generateAudio = this.clonedI2vProParameters.generateAudio;
		const perSecondPriceItem = lookupVideoPerSecondPriceItem(this.config, VideoPricingTaskType.I2vPro, {
			model,
			mode,
			generateAudio
		});
		if (perSecondPriceItem == null) return [];
		const itemKey = `i2v-pro-per-second-${model ?? "unknown"}-${mode ?? "unknown-mode"}-${generateAudio ? "audio" : "no-audio"}`;
		const actualBasePrice = this.getActualBasePrice();
		const actualTotal = Math.trunc(perSecondPriceItem.price * duration);
		const configuredOriginalTotal = Math.trunc((perSecondPriceItem.originalPrice ?? perSecondPriceItem.price) * duration);
		const originalTotal = Math.max(configuredOriginalTotal, actualTotal);
		const results = [{
			cumulative: (i) => i + (originalTotal - actualBasePrice),
			item: {
				key: itemKey,
				name: "i2v pro per second pricing",
				spec: `${duration}s`
			}
		}];
		if (originalTotal > actualTotal) results.push({
			cumulative: (i) => i - (originalTotal - actualTotal),
			item: {
				key: `${itemKey}-discount`,
				name: "i2v pro per second pricing discount",
				spec: `${duration}s`
			}
		});
		return results;
	}
	getPriceList() {
		const actualBasePrice = this.getActualBasePrice();
		const priceList = cloneParameters(this.config.i2vProPriceList ?? []);
		priceList.push({
			id: "unknown",
			parameters: {},
			price: 5e4
		});
		priceList.forEach((p) => {
			p.price -= actualBasePrice;
			if (p.originalPrice) p.originalPrice -= actualBasePrice;
		});
		return priceList;
	}
	_addAudioPrice() {
		const clonedI2vProParameters = this.clonedI2vProParameters;
		clonedI2vProParameters.generateAudio = clonedI2vProParameters.generateAudio ?? false;
		if (clonedI2vProParameters.model) {
			const [realModelId] = (clonedI2vProParameters.model ?? "").split("-");
			clonedI2vProParameters.model = realModelId;
		}
	}
	lookupI2vProPrice(sceneId) {
		const priceList = this.getPriceList();
		const parameters = this.clonedI2vProParameters;
		const matchedPrices = priceList.filter((p) => {
			if (p.sceneId !== sceneId) return false;
			return Object.entries(p.parameters).every(([key, value]) => parameters[key] === value);
		});
		if (!matchedPrices.length) return;
		return matchedPrices.sort((a, b) => Object.keys(b.parameters).length - Object.keys(a.parameters).length)[0];
	}
	calcOriginalPrice() {
		const originalPriceItem = this.lookupI2vProPrice();
		if (originalPriceItem) {
			const id = originalPriceItem.id;
			const price = originalPriceItem.originalPrice ?? originalPriceItem.price;
			return {
				cumulative: (i) => i + price,
				item: {
					key: `i2v-pro-${id}`,
					name: `i2v pro ${id}`
				}
			};
		}
	}
	calcDiscountedPrice() {
		const originalPriceItem = this.lookupI2vProPrice();
		if (originalPriceItem) {
			const originalPrice = originalPriceItem.originalPrice ?? originalPriceItem.price;
			const discountedPrice = this.lookupI2vProPrice(this.parameters.sceneId);
			if (discountedPrice?.price && discountedPrice?.price !== originalPrice) {
				const { id, sceneId, price: actualPrice } = discountedPrice;
				const price = originalPrice - actualPrice;
				return {
					cumulative: (i) => i - price,
					item: {
						key: `i2v-pro-${id}-${sceneId}-discount`,
						name: `i2v pro ${id} ${sceneId} discount`
					}
				};
			}
		}
	}
};
var T2I2VPricing = class extends BasePricing {
	getT2i2vParameters() {
		const { t2i2v } = this.parameters;
		return {
			...this.config.t2i2vDefaultParameters,
			...t2i2v
		};
	}
	isLtx23T2i2v() {
		const model = this.getT2i2vParameters().model;
		if (typeof model !== "string") return false;
		const [realModel] = model.split("-");
		return realModel === INHOUSE_LTX23_MODEL;
	}
	getActualBasePrice() {
		const { basePrice, modelType } = this.config;
		if (isDITModelType(modelType)) {
			const { width, height, samplingSteps } = this.parameters;
			return basePrice * ([
				[768, 1280],
				[1280, 768],
				[1024, 1024]
			].some(([w, h]) => w === width && h === height) && samplingSteps >= 20 && samplingSteps <= 25 ? 1.5 : 1.6);
		}
		return basePrice;
	}
	calcGenerateImageBasePrice() {
		const actualBasePrice = this.getActualBasePrice();
		return {
			cumulative: () => actualBasePrice,
			item: {
				key: "generate-image",
				name: "generate image"
			}
		};
	}
	getFinalPrice() {
		this.meter.addResult(this.calcGenerateImageBasePrice());
		for (const result of this.calcEditPrice()) this.meter.addResult(result);
		this.meter.addResult(this.calcChatBaseAdjustment());
		this.meter.addResult(this.calcVideoUpscalePrice());
		for (const result of this.calcI2vPrice()) this.meter.addResult(result);
		this.meter.addResult(this.calcLtx23ImageBaseAdjustment());
		return this.meter;
	}
	calcEditPrice() {
		const { chat } = this.parameters;
		if (!chat) return [];
		const editPricing = new EditPricing(this.parameters, this.config);
		const chatPrice = editPricing["getChatPrice"]();
		const results = [];
		results.push(editPricing.calcChatBasePrice(chatPrice));
		const discount = editPricing.calcChatDiscountPrice(chatPrice);
		if (discount) results.push(discount);
		const inputImages = editPricing.calcInputImageCountPrice(chatPrice);
		if (inputImages) results.push(inputImages);
		const resolution = editPricing.calcOutputResolutionPrice(chatPrice);
		if (resolution) results.push(resolution);
		return results;
	}
	calcChatBaseAdjustment() {
		const { chat } = this.parameters;
		if (!chat) return;
		const actualBasePrice = this.config.basePrice;
		return {
			cumulative: (i) => i + actualBasePrice,
			item: {
				key: "t2i2v-chat-base-adjustment",
				name: "t2i2v chat base adjustment",
				hidden: true
			}
		};
	}
	calcVideoUpscalePrice() {
		const { upscale } = this.parameters;
		if ((upscale ?? 1) > 1) return {
			cumulative: (i) => i + 6e3,
			item: {
				key: "t2i2v-upscale",
				name: "t2i2v upscale"
			}
		};
	}
	calcLtx23ImageBaseAdjustment() {
		if (this.parameters.chat || !this.isLtx23T2i2v()) return;
		const actualBasePrice = this.getActualBasePrice();
		return {
			cumulative: (i) => i + actualBasePrice,
			item: {
				key: "t2i2v-ltx23-image-base-adjustment",
				name: "t2i2v ltx23 image base adjustment",
				hidden: true
			}
		};
	}
	calcI2vPrice() {
		const rest = {
			...this.parameters,
			t2i2v: void 0
		};
		const t2i2vParameters = this.getT2i2vParameters();
		const i2vProPricing = new I2VProPricing({
			...rest,
			i2vPro: t2i2vParameters,
			t2i2v: void 0
		}, this.config);
		i2vProPricing._addAudioPrice();
		const results = [];
		const perSecondPriceResults = i2vProPricing.calcPerSecondPrice();
		if (perSecondPriceResults.length) {
			results.push(...perSecondPriceResults);
			return results;
		}
		const originalPrice = i2vProPricing.calcOriginalPrice();
		if (originalPrice) results.push(originalPrice);
		const discountedPrice = i2vProPricing.calcDiscountedPrice();
		if (discountedPrice) results.push(discountedPrice);
		return results;
	}
};
//#endregion
export { I2VProPricing, T2I2VPricing };
